import { IntlShape } from "react-intl";

import { useFoodManufacturerOrganization } from "../services/useOrganizationFeatures";
import assertNever from "../util/assertNever";
import { ConvertableUnit, Unit } from "../util/units";
import { FunctionalUnit } from "./functionalUnits";
import { ImpactCategory } from "./impactCategories";
import {
  RelatableEquivalent,
  relatableEquivalents,
} from "./relatableEquivalents";

type Impact = {
  effects: {
    ghgPerKg: number | null | undefined;
    ghgPerRootRecipeServing: number | null | undefined;
    landUsePerKg: number | null | undefined;
    landUsePerRootRecipeServing: number | null | undefined;
    waterUsePerKg: number | null | undefined;
    waterUsePerRootRecipeServing: number | null | undefined;
  } | null;
};

export type InternalBaseUnits = {
  unitToConvert: ConvertableUnit;
  otherBaseUnits: Unit[];
};

export default interface BaseEffectType {
  decimals: number;
  equivalent?: RelatableEquivalent;
  title: (intl: IntlShape) => string;
  unit: (intl: IntlShape) => string;
  // note that sometimes units will use this and sometimes they'll use the code from frontend/src/util/units.tsx
  internalBaseUnits: InternalBaseUnits;
}

export interface EffectType extends BaseEffectType {
  largeUnit: (intl: IntlShape) => string;
  largeUnitConversionFactor: number;
  // note that sometimes units will use this and sometimes they'll use the code from frontend/src/util/units.tsx
}

export interface EffectTypeAbsoluteValue extends BaseEffectType {
  get: (impact: Impact | null) => number | null;
  getQuantity: (ingredientImpact: {
    quantity: number | null;
    quantityPerRootRecipeServing: number | null;
  }) => number | null;
  per: (intl: IntlShape) => string;
  impactCategoryUnit: (intl: IntlShape) => string;
  internalPer: string;
}

const ghg: EffectType = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghg/title",
      defaultMessage: "GHG Emissions",
    }),
  unit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghg/unitString",
      defaultMessage: "kg CO\u2082e",
    }),
  decimals: 2,
  equivalent: relatableEquivalents.ghg.smartphoneCharge,
  largeUnit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghg/largeUnitString",
      defaultMessage: "t CO\u2082e",
    }),
  largeUnitConversionFactor: 1_000,
  internalBaseUnits: { unitToConvert: "kg", otherBaseUnits: ["co2e"] },
};

const ghgPerKg: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerKg/title",
      defaultMessage: "GHG Intensity",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:ghgPerKg/unitString",
        defaultMessage: "{ghgUnitString} / kg",
      },
      { ghgUnitString: ghg.unit(intl) }
    ),
  decimals: 2,
  equivalent: ghg.equivalent,
  get: (impact) => impact?.effects?.ghgPerKg ?? null,
  getQuantity: () => 1, // 1kg
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerKg/per",
      defaultMessage: "kg",
    }),
  impactCategoryUnit: ghg.unit,
  internalBaseUnits: ghg.internalBaseUnits,
  internalPer: "kg",
};

const ghgPerTonne: EffectTypeAbsoluteValue = {
  title: ghgPerKg.title,
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:ghgPerTonne/unitString",
        defaultMessage: "{ghgLargeUnit} / t",
      },
      { ghgLargeUnit: ghg.unit(intl) }
    ),
  decimals: 2,
  equivalent: ghg.equivalent,
  get: (impact) => null,
  getQuantity: () => 1, // 1tonne
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerTonne/per",
      defaultMessage: "t",
    }),
  impactCategoryUnit: ghg.largeUnit,
  internalBaseUnits: ghg.internalBaseUnits,
  internalPer: "t",
};

const ghgPerServing: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerServing/title",
      defaultMessage: "GHG Per Serving",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerServing/per",
      defaultMessage: "serving",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:ghgPerServing/unitString",
        defaultMessage: "{ghgUnitString} / serving",
      },
      { ghgUnitString: ghg.unit(intl) }
    ),
  decimals: 2,
  equivalent: relatableEquivalents.ghg.smartphoneCharge,
  get: (impact) => impact?.effects?.ghgPerRootRecipeServing ?? null,
  getQuantity: (ingredientImpact) =>
    ingredientImpact.quantityPerRootRecipeServing,
  impactCategoryUnit: ghg.unit,
  internalBaseUnits: ghg.internalBaseUnits,
  internalPer: "serving",
};

const ghgPerProduct: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerProduct/title",
      defaultMessage: "GHG Per Product",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:ghgPerProduct/per",
      defaultMessage: "product",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:ghgPerProduct/unitString",
        defaultMessage: "{ghgUnitString} / product",
      },
      { ghgUnitString: ghg.unit(intl) }
    ),
  decimals: 2,
  equivalent: relatableEquivalents.ghg.smartphoneCharge,
  get: (impact) => impact?.effects?.ghgPerRootRecipeServing ?? null,
  getQuantity: (ingredientImpact) =>
    ingredientImpact.quantityPerRootRecipeServing,
  impactCategoryUnit: ghg.unit,
  internalBaseUnits: ghg.internalBaseUnits,
  internalPer: "product",
};

const landUse: EffectType = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUse/title",
      defaultMessage: "Land Use",
    }),
  unit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUse/unitString",
      defaultMessage: "m\u00B2yr",
    }),
  decimals: 2,
  largeUnit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUse/largeUnitString",
      defaultMessage: "k(m\u00B2)year",
    }),
  largeUnitConversionFactor: 1_000,
  internalBaseUnits: { unitToConvert: "m2", otherBaseUnits: ["year"] },
};

const landUsePerKg: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerKg/title",
      defaultMessage: "Land Use Intensity",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerKg/per",
      defaultMessage: "kg",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:landUsePerKg/unitString",
        defaultMessage: "{landUseUnitString} / kg",
      },
      { landUseUnitString: landUse.unit(intl) }
    ),
  decimals: 2,
  get: (impact) => impact?.effects?.landUsePerKg ?? null,
  getQuantity: () => 1, // 1kg
  impactCategoryUnit: landUse.unit,
  internalBaseUnits: landUse.internalBaseUnits,
  internalPer: "kg",
};

const landUsePerTonne: EffectTypeAbsoluteValue = {
  title: landUsePerKg.title,
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:landUsePerTonne/unitString",
        defaultMessage: "{landUseLargeUnitString} / t",
      },
      { landUseLargeUnitString: landUse.largeUnit(intl) }
    ),
  decimals: 2,
  equivalent: ghg.equivalent,
  get: (impact) => null,
  getQuantity: () => 1, // 1tonne
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerTonne/per",
      defaultMessage: "t",
    }),
  impactCategoryUnit: landUse.largeUnit,
  internalBaseUnits: landUse.internalBaseUnits,
  internalPer: "t",
};

const getLandUsePerServing = (impact: Impact | null) =>
  impact?.effects?.landUsePerRootRecipeServing ?? null;
const getLandUsePerServingQuantity = (ingredientImpact: {
  quantityPerRootRecipeServing: number | null;
}) => ingredientImpact.quantityPerRootRecipeServing;

const landUsePerServing: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerServing/title",
      defaultMessage: "Land Use Per Serving",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerServing/per",
      defaultMessage: "serving",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:landUsePerServing/unitString",
        defaultMessage: "{landUseUnitString} / serving",
      },
      { landUseUnitString: landUse.unit(intl) }
    ),
  decimals: 2,
  get: getLandUsePerServing,
  getQuantity: getLandUsePerServingQuantity,
  impactCategoryUnit: landUse.unit,
  internalBaseUnits: landUse.internalBaseUnits,
  internalPer: "serving",
};

const landUsePerProduct: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerProduct/title",
      defaultMessage: "Land Use Per Product",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:landUsePerProduct/per",
      defaultMessage: "product",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:landUsePerProduct/unitString",
        defaultMessage: "{landUseUnitString} / product",
      },
      { landUseUnitString: landUse.unit(intl) }
    ),
  decimals: 2,
  get: getLandUsePerServing,
  getQuantity: getLandUsePerServingQuantity,
  impactCategoryUnit: landUse.unit,
  internalBaseUnits: landUse.internalBaseUnits,
  internalPer: "product",
};

const waterUse: EffectType = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUse/title",
      defaultMessage: "Water Use",
    }),
  unit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUse/unitString",
      defaultMessage: "L",
    }),
  decimals: 0,
  largeUnit: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUse/largeUnitString",
      defaultMessage: "m\u00B3",
    }),
  largeUnitConversionFactor: 1_000,
  internalBaseUnits: { unitToConvert: "L", otherBaseUnits: [] },
};

const waterUsePerKg: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerKg/title",
      defaultMessage: "Water Use Intensity",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerKg/per",
      defaultMessage: "kg",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:waterUsePerKg/unitString",
        defaultMessage: "{waterUseUnitString} / kg",
      },
      { waterUseUnitString: waterUse.unit(intl) }
    ),
  decimals: 2,
  get: (impact) => impact?.effects?.waterUsePerKg ?? null,
  getQuantity: () => 1, // 1kg
  impactCategoryUnit: waterUse.unit,
  internalBaseUnits: waterUse.internalBaseUnits,
  internalPer: "kg",
};

const waterUsePerTonne: EffectTypeAbsoluteValue = {
  title: waterUsePerKg.title,
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:waterUsePerTonne/unitString",
        defaultMessage: "{waterUseLargeUnitString} / t",
      },
      { waterUseLargeUnitString: waterUse.largeUnit(intl) }
    ),
  decimals: 2,
  equivalent: ghg.equivalent,
  get: (impact) => null,
  getQuantity: () => 1, // 1tonne
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerTonne/per",
      defaultMessage: "t",
    }),
  impactCategoryUnit: waterUse.largeUnit,
  internalBaseUnits: waterUse.internalBaseUnits,
  internalPer: "t",
};

const getWaterUsePerServing = (impact: Impact | null) =>
  impact?.effects?.waterUsePerRootRecipeServing ?? null;
const getWaterUsePerServingQuantity = (ingredientImpact: {
  quantityPerRootRecipeServing: number | null;
}) => ingredientImpact.quantityPerRootRecipeServing;

const waterUsePerServing: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerServing/title",
      defaultMessage: "Water Use Per Serving",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerServing/per",
      defaultMessage: "serving",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:waterUsePerServing/unitString",
        defaultMessage: "{waterUseUnitString} / serving",
      },
      { waterUseUnitString: waterUse.unit(intl) }
    ),
  decimals: 2,
  get: getWaterUsePerServing,
  getQuantity: getWaterUsePerServingQuantity,
  impactCategoryUnit: waterUse.unit,
  internalBaseUnits: waterUse.internalBaseUnits,
  internalPer: "serving",
};

const waterUsePerProduct: EffectTypeAbsoluteValue = {
  title: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerProduct/title",
      defaultMessage: "Water Use Per Product",
    }),
  per: (intl) =>
    intl.formatMessage({
      id: "domain/EffectType:waterUsePerProduct/per",
      defaultMessage: "product",
    }),
  unit: (intl) =>
    intl.formatMessage(
      {
        id: "domain/EffectType:waterUsePerProduct/unitString",
        defaultMessage: "{waterUseUnitString} / product",
      },
      { waterUseUnitString: waterUse.unit(intl) }
    ),
  decimals: 2,
  get: getWaterUsePerServing,
  getQuantity: getWaterUsePerServingQuantity,
  impactCategoryUnit: waterUse.unit,
  internalBaseUnits: waterUse.internalBaseUnits,
  internalPer: "product",
};

export const impactCategoryToEffectType = (
  impactCategory: ImpactCategory
): EffectType => {
  if (impactCategory === ImpactCategory.GHG) {
    return ghg;
  } else if (impactCategory === ImpactCategory.LAND_USE) {
    return landUse;
  } else if (impactCategory === ImpactCategory.WATER_USE) {
    return waterUse;
  } else {
    assertNever(impactCategory, "Unsupported ImpactCategory");
  }
};

export const impactCategoryToEffectTypePerKg = (
  impactCategory: ImpactCategory
): EffectTypeAbsoluteValue => {
  if (impactCategory === ImpactCategory.GHG) {
    return ghgPerKg;
  } else if (impactCategory === ImpactCategory.LAND_USE) {
    return landUsePerKg;
  } else if (impactCategory === ImpactCategory.WATER_USE) {
    return waterUsePerKg;
  } else {
    assertNever(impactCategory, "Unsupported ImpactCategory");
  }
};

export const impactCategoryToEffectTypePerLargeUnit = (
  impactCategory: ImpactCategory
): EffectTypeAbsoluteValue => {
  if (impactCategory === ImpactCategory.GHG) {
    return ghgPerTonne;
  } else if (impactCategory === ImpactCategory.LAND_USE) {
    return landUsePerTonne;
  } else if (impactCategory === ImpactCategory.WATER_USE) {
    return waterUsePerTonne;
  } else {
    assertNever(impactCategory, "Unsupported ImpactCategory");
  }
};

export const useImpactCategoryEffectTypePerPortion = (
  impactCategory: ImpactCategory
): EffectTypeAbsoluteValue => {
  const foodManufacturerOrganization = useFoodManufacturerOrganization();
  if (impactCategory === ImpactCategory.GHG) {
    return foodManufacturerOrganization ? ghgPerProduct : ghgPerServing;
  } else if (impactCategory === ImpactCategory.LAND_USE) {
    return foodManufacturerOrganization ? landUsePerProduct : landUsePerServing;
  } else if (impactCategory === ImpactCategory.WATER_USE) {
    return foodManufacturerOrganization
      ? waterUsePerProduct
      : waterUsePerServing;
  } else {
    assertNever(impactCategory, "Unsupported ImpactCategory");
  }
};

export const useEffectTypeForImpactCategoryPerFunctionalUnit = (
  impactCategory: ImpactCategory,
  functionalUnit: FunctionalUnit
) => {
  const impactCategoryEffectTypePerPortion =
    useImpactCategoryEffectTypePerPortion(impactCategory);
  if (functionalUnit === FunctionalUnit.KG) {
    return impactCategoryToEffectTypePerKg(impactCategory);
  } else if (functionalUnit === FunctionalUnit.PORTION) {
    return impactCategoryEffectTypePerPortion;
  } else {
    assertNever(functionalUnit, "Unsupported FunctionalUnit");
  }
};

export const effectTypes = {
  ghg,
  ghgPerKg,
  ghgPerProduct,
  ghgPerServing,
  landUse,
  landUsePerKg,
  landUsePerProduct,
  landUsePerServing,
  waterUse,
  waterUsePerKg,
  waterUsePerServing,
  waterUsePerProduct,
};
